import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-primary-600 text-primary-foreground shadow-3d hover:bg-primary-700 hover:shadow-3d-hover transition-all duration-300 ease-in-out",
        destructive:
          "bg-destructive text-destructive-foreground shadow-3d hover:bg-destructive/90 hover:shadow-3d-hover transition-all duration-300 ease-in-out",
        outline:
          "border-2 border-primary-600 bg-transparent text-primary-600 shadow-3d hover:bg-primary-600/10 hover:shadow-3d-hover transition-all duration-300 ease-in-out",
        secondary:
          "bg-secondary text-secondary-foreground shadow-3d hover:bg-secondary/80 hover:shadow-3d-hover transition-all duration-300 ease-in-out",
        ghost: "hover:bg-primary-600/10 hover:text-primary-600 transition-all duration-300 ease-in-out",
        link: "text-primary-600 underline-offset-4 hover:underline hover:text-primary-700 transition-colors duration-300",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };